import { FC, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { actions, urls } from '../../../dispatch';
import { dispatch } from '../../../dispatch/dispatch';
import { IErrorResponse } from '../../../interfaces/responses/ErrorResponse.interface';
import { ISignUpFormData } from '../../../interfaces/formData/SignupFormData.interface';
import { ISignupFormDataValidation } from '../../../interfaces/validations/signupFormValidation.interface';
import { signupFormValidation } from '../../../validations/signupFormValidation';
import { io } from 'socket.io-client';
import ContextStore from '../../../hooks/ContextStore';
import { IUserResponse } from '../../../interfaces/responses/UserResponse.interface';
import { Zap, Eye, EyeOff, AlertCircle, ArrowLeft, Clock } from 'lucide-react';

// ── All existing business logic is 100% unchanged ─────────────────────────
export const Signup: FC = () => {
  const navigate = useNavigate();
  const { contextStore, setContextStore } = useContext(ContextStore);
  const [formData, setFormData] = useState<ISignUpFormData>({ email: '', password: '', password2: '' });
  const [errors, setErrors] = useState<ISignupFormDataValidation | undefined>();
  const [showPw, setShowPw]   = useState(false);
  const [showPw2, setShowPw2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const [apiErr,  setApiErr]  = useState('');

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    setApiErr('');
    const vErrors = signupFormValidation(formData);
    setErrors(vErrors);
    if (vErrors.password || vErrors.password2 || vErrors.email) return;

    setLoading(true);
    contextStore.setShowSpinner(true);
    const resp: any = await dispatch(
      actions.signup,
      { email: formData.email, password: formData.password }
    );

    if (resp?.token) {
      // First user (admin) — log in immediately
      const userResponse: IUserResponse | IErrorResponse = await dispatch(actions.getUser, {}, resp.token);
      if ('error' in userResponse) {
        contextStore.setShowSpinner(false);
        setLoading(false);
        return;
      }
      localStorage.setItem('token', resp.token);
      const socket = io(urls.baseUrlSocketApi, { reconnectionDelayMax: 10000, auth: { token: resp.token } });
      setContextStore((past: any) => ({ ...past, user: userResponse, token: resp.token, socket }));
      contextStore.setShowSpinner(false);
      navigate('/');
    } else if (resp?.pending) {
      // Regular user — show pending screen
      contextStore.setShowSpinner(false);
      setLoading(false);
      setPending(true);
    } else {
      contextStore.setShowSpinner(false);
      setLoading(false);
      setApiErr(resp?.error?.message || 'Something went wrong.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ── JSX redesigned with Tailwind — logic above unchanged ──────────────
  const inputCls = (hasError?: string) =>
    `w-full rounded-lg border px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground bg-background outline-none transition-colors focus:ring-2 focus:ring-ring focus:border-transparent ${
      hasError ? 'border-red-400 focus:ring-red-300' : 'border-border hover:border-muted-foreground'
    }`;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">

      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 h-80 w-80 rounded-full bg-primary/5 blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm">

        <div className="rounded-2xl border border-border bg-card shadow-card-md p-8">

          {/* Logo */}
          <div className="mb-8 flex flex-col items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
              <Zap className="h-6 w-6 text-primary" strokeWidth={2.5} />
            </div>
            <div className="text-center">
              <h1 className="text-xl font-bold text-foreground">
                {pending ? 'Request submitted' : 'Request access'}
              </h1>
              <p className="mt-0.5 text-sm text-muted-foreground">
                {pending ? 'Waiting for admin approval' : 'Create your JumiaDash account'}
              </p>
            </div>
          </div>

          {/* Pending screen */}
          {pending && (
            <div className="flex flex-col items-center gap-4 py-2">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                <Clock className="h-7 w-7 text-amber-600" />
              </div>
              <div className="text-center space-y-1.5">
                <p className="text-sm font-medium text-foreground">Your request is pending</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  The admin will review your access request shortly.
                  You'll be able to sign in once approved.
                </p>
              </div>
              <button
                onClick={() => navigate('/login')}
                className="mt-2 w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary/90 transition-all"
              >
                Back to Sign in
              </button>
            </div>
          )}

          {!pending && apiErr && (
            <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-xs text-red-700">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />{apiErr}
            </div>
          )}

          {!pending && <form onSubmit={handleSubmit} className="space-y-4">

            {/* Email */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">Email</label>
              <input
                value={formData.email} type="text" name="email"
                placeholder="admin@gmail.com" onChange={handleChange}
                className={inputCls(errors?.email)}
              />
              {errors?.email && (
                <div className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />{errors.email}
                </div>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">Password</label>
              <div className="relative">
                <input
                  value={formData.password} type={showPw ? 'text' : 'password'}
                  name="password" placeholder="Create a password" onChange={handleChange}
                  className={inputCls(errors?.password) + ' pr-10'}
                />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors?.password && (
                <div className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />{errors.password}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-foreground">Confirm Password</label>
              <div className="relative">
                <input
                  value={formData.password2} type={showPw2 ? 'text' : 'password'}
                  name="password2" placeholder="Re-type your password" onChange={handleChange}
                  className={inputCls(errors?.password2) + ' pr-10'}
                />
                <button type="button" onClick={() => setShowPw2(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  {showPw2 ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors?.password2 && (
                <div className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />{errors.password2}
                </div>
              )}
            </div>

            <button
              type="submit" disabled={loading}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow hover:bg-primary/90 active:scale-[0.98] disabled:opacity-60 transition-all duration-150 mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Creating account…
                </span>
              ) : 'Create account'}
            </button>
          </form>}

          {!pending && (
            <p className="mt-6 text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <button onClick={() => navigate('/login')} className="font-semibold text-primary hover:underline">
                Sign in
              </button>
            </p>
          )}
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          JumiaDash — Seller Analytics Platform
        </p>
      </div>
    </div>
  );
};
